/**
 * TEMPORARY FLAGS
 * Generally used for gating access to features while we beta test and / or
 * the feature needs to go through design review
 */
export const SALESFORCE_APP_EARLY_ADOPTER = 'early_adopter_salesforce_widget';
export const MULTI_SELECT_SF = 'multi_select_sf';
export const SNOWFLAKE_PULL_LIMIT = 'snowflake_pull_limits';
export const TEMP_PP_UNLIMITED_CUSTOM = 'pp_unlimited_custom_pops';
export const TEMP_LAST_ACTIVITY = 'temp_last_activity';
export const TEMP_EXCLUDE_OFFLINE_PARTNERS = 'temp_exclude_offline_partners';
export const TEMP_HAS_BAD_HUBSPOT_PUSH_AUTH =
  'temp_has_bad_hubspot_push_authentication';
export const TEMP_PARTNER_VISIBILITY = 'temp_partner_visibility';
export const TEMP_ALLOW_ADDING_TO_BIG_CSV = 'temp_allow_adding_data_to_big_csv';
export const TEMP_DEAL_CONTACT_ROLE_FIELDS_FILTERABLE =
  'temp_deal_contact_role_fields_filterable';
export const TEMP_UPDATED_DATA_PUSH_POP_TYPES =
  'temp_updated_data_push_pop_types';
export const SCIM_FOR_SUPERNODE = 'scim_for_supernode';
export const TEMP_PARTNER_SCORE_ACCESS = 'temp_partner_score_access';
export const TEMP_ADD_OPEN_DATA_PARTNER = 'temp_add_open_data_partner';
export const TEMP_PARTNER_TRACKER_ACCESS = 'temp_partner_tracker_access';
export const TEMP_NEW_ONBOARDING_SMART = 'temp_new_onboarding_smart';
export const TEMP_API_KEYS = 'temp_api_keys';
export const TEMP_MCP_USAGE_IN_APP_TRACKING = 'temp_mcp_usage_in_app_tracking';
export const TEMP_TIMELINE_ACTIVITY_LISTS = 'temp_timeline_activity_lists';
export const TEMP_SMART_FIELD_AND_POP = 'temp_smart_field_and_pop';
export const TEMP_SELF_SERVICE_CREDIT_LIMITS =
  'temp_self_service_credit_limits';
export const TEMP_SHARING_SETTINGS_BY_PARTNERS =
  'temp_sharing_settings_by_partners';
export const TEMP_DEPRECATED_MOP = 'temp_deprecated_mop';
export const TEMP_OFFLINE_PARTNER_GOOGLE_SHEETS =
  'temp_offline_partner_google_sheets';
export const TEMP_READ_POPULATION_MATCHES_V5 =
  'temp_read_population_matches_v5';
export const TEMP_ENABLE_OVERLAP_MOVEMENT = 'temp_enable_overlap_movement';
export const TEMP_TEAM_DEFAULT_PRESET = 'temp_team_default_preset';
export const TEMP_SHOW_OKTA_XAA = 'temp_show_okta_xaa';
export const XB_COUPON_CONNECTOR_PLATFORM_FEE_MONTHLY =
  '_xb_coupon_connector_platform_fee_monthly';
export const XB_COUPON_CONNECTOR_PLATFORM_FEE_ANNUAL =
  '_xb_coupon_connector_platform_fee_annual';

/**
 * PERMANENT FLAGS
 * These flags are not globally released, they are turned on a case-by-case basis.
 * They are typically used for individual paid features
 */
export const SSO = 'sso';
export const REST_API = 'rest_api';
export const SNOWFLAKE_PUSH = 'snowflake_push';
export const DATABRICKS_PUSH = 'databricks_push';
export const HUBSPOT_EXPORT_SCOPE = 'hubspot_export_scope';
export const SALESFORCE_APP_PUSH = 'salesforce_app_push';
// The new flag is used to control the legacy Salesforce app push feature only for OKTA
export const SALESFORCE_LEGACY_APP_PUSH_OVERRIDE =
  'salesforce_legacy_app_push_override';
export const SALESFORCE_PUSH_CONFIG = 'salesforce_push_config';
export const AUDIT_LOG = 'audit_log';
export const ATTRIBUTION_ACCESS_OVERRIDE =
  'attribution_access'; /* Supernode customers automatically get attribution */
export const PREVENT_DEFAULT_PARTNERSTACK_COL =
  'prevent_default_partnerstack_col'; /* does not add PartnerStack Column as a default in Reports/Shared Lists when enabled (requested for demo orgs) */
export const EXCLUDE_OTHER_SEGMENT_FROM_ACCOUNT_MAPPING =
  'exclude_other_segment_from_account_mapping'; /* leaves the Other segment out of the default Account Mapping list filters when enabled (requested for demo orgs) */
export const OVER_SEAT_QUOTA_ALLOWED = 'over_seat_quota_allowed';
export const PARTNERSTACK_PARTNERSHIP_KEY = 'partnerstack_partnership_key';
export const BIG_CSV_FILE_LIMIT_500MB = 'big_csv_file_limit_500MB';
export const SHARING_OVERRIDES_ENABLED = 'sharing_overrides_enabled';
export const SALESFORCE_PUSH_JWT = 'salesforce_push_jwt';
export const CONFIGURE_PARTNER_TAGS_PUSH_TO_SALESFORCE =
  'configure_partner_tags_push_to_salesforce';
export const CLOUDFLARE_SALESFORCE_PROXY = 'cloudflare_salesforce_proxy';

// Partner cloud flags
export const PC_INTEGRATIONS = {
  CLARI: 'pc_clari',
  DYNAMICS: 'pc_dynamics',
  GONG: 'pc_gong',
  HUBSPOT: 'pc_hubspot',
  HUBSPOT_V2: 'pc_hubspot_v2',
  MARKETO: 'pc_marketo',
  SALESFORCE_ATTRIBUTION_PUSH: 'pc_salesforce_attribution',
  TORCHLITE: 'pc_torchlite',
  TRAY: 'pc_tray',
} as const;

export const FEATURE_FLAG = {
  MULTI_SELECT_SF,
  SALESFORCE_APP_EARLY_ADOPTER,
  SNOWFLAKE_PULL_LIMIT,
  TEMP_ALLOW_ADDING_TO_BIG_CSV,
  TEMP_DEAL_CONTACT_ROLE_FIELDS_FILTERABLE,
  TEMP_EXCLUDE_OFFLINE_PARTNERS,
  TEMP_HAS_BAD_HUBSPOT_PUSH_AUTH,
  TEMP_LAST_ACTIVITY,
  TEMP_PARTNER_VISIBILITY,
  TEMP_PP_UNLIMITED_CUSTOM,
  TEMP_CALCULATE_SEGMENTS_OVERLAP_COUNTS:
    'temp_calculate_segments_overlap_counts',
  TEMP_UPDATED_DATA_PUSH_POP_TYPES,
  TEMP_GENPIPE_NORMALIZE_INDUSTRY: 'temp_genpipe_normalize_industry',
  TEMP_SFDC_JWT_FIELDS: 'salesforce_jwt_authorization',
  OMNI_ENABLED: 'omni_enabled',
  TEMP_GENPIPE_GREENFIELDS: 'temp_genpipe_greenfields',
  TEMP_FIELD_MAPPING_IN_DATA_SOURCE_SETTINGS:
    'temp_field_mapping_in_data_source_settings',
  SCIM_FOR_SUPERNODE,
  TEMP_PARTNER_SCORE_ACCESS,
  TEMP_GREENFIELD_DEAL_WEBHOOKS: 'temp_greenfield_deal_webhooks',
  TEMP_OWN_DEAL_SIGNALS: 'temp_own_deal_signals',
  TEMP_MENTIONS_IN_LISTS: 'temp_mentions_in_lists',
  TEMP_ADD_OPEN_DATA_PARTNER,
  TEMP_PARTNER_TRACKER_ACCESS,
  TEMP_NEW_ONBOARDING_SMART,
  TEMP_API_KEYS,
  TEMP_API_CONTROLS: 'temp_api_controls',
  TEMP_MCP_USAGE_IN_APP_TRACKING,
  MCP_DIRECT_OAUTH: 'mcp_direct_oauth',
  TEMP_AGENTS: 'temp_agents',
  TEMP_NEW_MUTUAL_CUSTOMER_FROM_SIGNAL: 'temp_new_mutual_customer_from_signal',
  TEMP_AUTOMATIONS_TEMPLATES: 'temp_automations_templates',
  TEMP_IRP_V2: 'temp_irp_v2',
  TEMP_TIMELINE_ACTIVITY_LISTS,
  TEMP_INTEGRATION_USER: 'temp_integration_user',
  TEMP_SMART_FIELD_AND_POP,
  TEMP_DEPRECATED_MOP,
  TEMP_OFFLINE_PARTNER_GOOGLE_SHEETS,
  TEMP_READ_POPULATION_MATCHES_V5,
  TEMP_ENABLE_OVERLAP_MOVEMENT,
  TEMP_TEAM_DEFAULT_PRESET,
  TEMP_MCP_METERING_UI: 'temp_mcp_metering_ui',
  TEMP_MCP_SOFT_LAUNCH: 'temp_mcp_soft_launch',
  NO_MCP_ACCESS: 'no_mcp_access',
  TEMP_MCP_PERMISSIONS: 'temp_mcp_permissions',
  TEMP_SELF_SERVICE_CREDIT_LIMITS,
  TEMP_AGENTS_CREDIT_METERING: 'temp_agents_credit_metering',
  TEMP_SHARING_SETTINGS_BY_PARTNERS,
  TEMP_ACTIVATION_TEAM_CC: 'temp_activation_team_cc',
  TEMP_ACCOUNT_PRODUCTS_SYNC: 'temp_account_products_sync',
  TEMP_ACCOUNT_PRODUCTS_GA: 'temp_account_products_ga',
  TEMP_CRM_WISHLIST_SYNC: 'temp_crm_wishlist_sync',
  TEMP_NUDGE_INVITATIONS: 'temp_nudge_invitations',
  XB_COUPON_CONNECTOR_PLATFORM_FEE_MONTHLY,
  XB_COUPON_CONNECTOR_PLATFORM_FEE_ANNUAL,
  AWS_PARTNER_CENTRAL: 'aws_partner_central',
  TEMP_SHOW_OKTA_XAA,
} as const;

export const ORGANIZATION_PAYMENT_REQUIRED = 'organization_payment_required';

export const PERMANENT_FLAGS = {
  ATTRIBUTION_ACCESS_OVERRIDE,
  AUDIT_LOG,
  BIG_CSV_FILE_LIMIT_500MB,
  CLOUDFLARE_SALESFORCE_PROXY,
  EXCLUDE_OTHER_SEGMENT_FROM_ACCOUNT_MAPPING,
  HUBSPOT_EXPORT_SCOPE,
  ORGANIZATION_PAYMENT_REQUIRED,
  OVER_SEAT_QUOTA_ALLOWED,
  PARTNERSTACK_PARTNERSHIP_KEY,
  PREVENT_DEFAULT_PARTNERSTACK_COL,
  REST_API,
  SALESFORCE_APP_PUSH,
  SALESFORCE_LEGACY_APP_PUSH_OVERRIDE,
  SALESFORCE_PUSH_CONFIG,
  SHARING_OVERRIDES_ENABLED,
  DATABRICKS_PUSH,
  SNOWFLAKE_PUSH,
  SSO,
  BLOCK_AI_CHAT_ACCESS: 'block_ai_chat_access',
  WEBHOOK_ACCESS: 'webhook_access',
  GENERATE_INTEGRATION_OVERLAPS: 'generate_integration_overlaps',
  SALESFORCE_PUSH_JWT,
  DELL_SPIL_FEED: 'dell_spil_feed',
  CONFIGURE_PARTNER_TAGS_PUSH_TO_SALESFORCE,
  EXTERNAL_FILE_DATA: 'external_file_data',
} as const;
